import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Activity, Server, Database, Shield, Globe, Wifi, Cpu } from 'lucide-react';

interface NetworkStatusProps {
  isOpen: boolean;
  onClose: () => void;
}

const NetworkStatus: React.FC<NetworkStatusProps> = ({ isOpen, onClose }) => {
  const [latency, setLatency] = useState(24);
  const [requests, setRequests] = useState(24500000);

  // Simulate live data fluctuations
  useEffect(() => {
    if (!isOpen) return;
    
    const latencyInterval = setInterval(() => {
      setLatency(prev => Math.max(12, Math.min(45, prev + (Math.random() - 0.5) * 15)));
    }, 800);

    const requestInterval = setInterval(() => {
      setRequests(prev => prev + Math.floor(Math.random() * 50));
    }, 200);

    return () => {
      clearInterval(latencyInterval);
      clearInterval(requestInterval);
    };
  }, [isOpen]);

  const services = [
    { name: 'Auth Gatekeeper', status: 'Operational', icon: Shield, region: 'Global' },
    { name: 'Zentix Cloud Nodes', status: 'Operational', icon: Server, region: 'EU-West' },
    { name: 'Neural Database', status: 'Operational', icon: Database, region: 'US-East' },
    { name: 'Edge Delivery Network', status: 'Operational', icon: Globe, region: 'Global' },
    { name: 'Crypto Payment Rail', status: 'Operational', icon: Wifi, region: 'Decentralized' },
    { name: 'AI Inference Engine', status: 'Operational', icon: Cpu, region: 'US-Central' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-lg"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="w-full max-w-2xl bg-[#050505] border border-white/10 rounded-2xl overflow-hidden shadow-[0_0_50px_-12px_rgba(6,182,212,0.2)] relative"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-3">
                    <div className="relative flex items-center justify-center w-8 h-8 rounded-full bg-cyan-500/10 border border-cyan-500/20">
                        <Activity className="text-cyan-400" size={16} />
                        <span className="absolute inset-0 rounded-full border border-cyan-400/30 animate-ping" />
                    </div>
                    <div>
                      <h2 className="text-lg font-display font-bold text-white tracking-wide">System Status</h2>
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                        <span className="text-xs text-green-500 font-mono">ALL SYSTEMS OPERATIONAL</span>
                      </div>
                    </div>
                </div>
                <button 
                  onClick={onClose} 
                  className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
                >
                    <X size={20} />
                </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
                {/* Metrics Grid */}
                <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-mono">Latency</div>
                        <div className="text-2xl font-mono text-cyan-400 flex items-baseline gap-1">
                          {Math.round(latency)}
                          <span className="text-sm text-cyan-500/50">ms</span>
                        </div>
                    </div>
                     <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-mono">Uptime</div>
                        <div className="text-2xl font-mono text-green-400">99.99%</div>
                    </div>
                     <div className="p-4 rounded-xl bg-white/[0.03] border border-white/5 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="text-xs text-gray-500 uppercase tracking-wider mb-1 font-mono">Requests</div>
                        <div className="text-2xl font-mono text-purple-400">{requests.toLocaleString()}</div>
                    </div>
                </div>

                {/* Status List */}
                <div className="space-y-3">
                    <h3 className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-4 pl-1">Infrastructure Health</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {services.map((s, i) => (
                          <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:bg-white/5 transition-colors group">
                              <div className="flex items-center gap-3">
                                  <div className="p-2 rounded-md bg-black border border-white/10 text-gray-400 group-hover:text-cyan-400 group-hover:border-cyan-500/30 transition-colors">
                                    <s.icon size={14} />
                                  </div>
                                  <div>
                                    <span className="text-sm font-medium text-gray-200 block">{s.name}</span>
                                    <span className="text-[10px] text-gray-600 font-mono uppercase">{s.region}</span>
                                  </div>
                              </div>
                              <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-green-500/10 border border-green-500/20">
                                  <span className="text-[10px] text-green-400 font-bold tracking-wide">OK</span>
                              </div>
                          </div>
                      ))}
                    </div>
                </div>
                
                 {/* Footer of modal */}
                 <div className="pt-4 border-t border-white/5 flex justify-between items-center text-[10px] font-mono text-gray-600">
                    <div className="flex gap-4">
                      <span>ID: ZTX-NODES-9904</span>
                      <span>ENV: PRODUCTION_ALPHA</span>
                    </div>
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse"></span>
                      LIVE STREAM
                    </span>
                 </div>
            </div>

            {/* Deco line */}
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-cyan-500 via-purple-600 to-cyan-500"></div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NetworkStatus;